import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { apiClient } from "src/api/client";
import { AccountBalanceSnapshot } from "src/api/types";
import closeIcon from "src/assets/icons/closeGray.svg";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { useAppDispatch } from "src/store/hooks";
import { closeModal, selectModalState } from "src/store/slices/modalSlice";
import { RootState } from "src/store/store";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

import styles from "../AccountingSetup/styles.module.css";

const today = () => new Date().toISOString().slice(0, 10);

export const BalanceSnapshotsModal = () => {
  const dispatch = useAppDispatch();
  const modal = useSelector((state: RootState) => selectModalState(state));
  const data = modal.data.balanceSnapshotsModal as
    | { accountId: number; accountName: string }
    | undefined;
  const [items, setItems] = useState<AccountBalanceSnapshot[]>([]);
  const [date, setDate] = useState(today());
  const [balance, setBalance] = useState("");
  const [editingId, setEditingId] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => dispatch(closeModal({ type: "balanceSnapshotsModal" }));
  const load = useCallback(async () => {
    if (!data?.accountId) return;
    try {
      setLoading(true);
      setError(null);
      setItems(await apiClient.getBalanceSnapshots(data.accountId));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [data?.accountId]);

  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const reset = () => {
    setDate(today());
    setBalance("");
    setEditingId(undefined);
  };

  const save = async () => {
    const numericBalance = Number(balance);
    if (!date) return setError("Date is required");
    if (!Number.isFinite(numericBalance)) return setError("Enter a valid signed balance");
    try {
      setSaving(true);
      setError(null);
      await apiClient.saveBalanceSnapshot(
        data.accountId,
        { as_of_date: date, balance: numericBalance },
        editingId,
      );
      reset();
      await load();
      window.dispatchEvent(new CustomEvent("accountUpdated"));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item: AccountBalanceSnapshot) => {
    setEditingId(item.id);
    setDate(item.as_of_date);
    setBalance(item.balance);
  };

  const remove = async (item: AccountBalanceSnapshot) => {
    if (!window.confirm(`Delete the balance snapshot for ${item.as_of_date}?`)) return;
    try {
      await apiClient.deleteBalanceSnapshot(data.accountId, item.id);
      if (editingId === item.id) reset();
      await load();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div className={styles.modal} role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div><h2>Account balances</h2><p>{data.accountName}. Use a negative balance for credit debt.</p></div>
          <button className={styles.closeButton} type="button" onClick={close} aria-label="Close"><img src={closeIcon} alt="" /></button>
        </div>
        <div className={styles.content}>
          <div className={styles.formGrid}>
            <label className={styles.field}><span>Balance date</span><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
            <label className={styles.field}><span>Signed balance</span><input type="number" step="0.01" value={balance} onChange={(event) => setBalance(event.target.value)} placeholder="0.00" /></label>
          </div>
          <ErrorBlock error={error} />
          <div className={styles.actions}>
            {editingId && <button className={styles.secondary} type="button" onClick={reset}>Cancel edit</button>}
            <button className={styles.button} type="button" onClick={save} disabled={saving}>{saving ? "Saving..." : editingId ? "Save changes" : "Add balance"}</button>
          </div>
          {loading ? <div className={styles.empty}>Loading balances...</div> : items.length === 0 ? <div className={styles.empty}>No balance snapshots yet.</div> : (
            <div className={styles.list}>
              {items.map((item) => (
                <div className={styles.row} key={item.id}>
                  <div><strong>{item.as_of_date}</strong>{item.is_reconciled === false && <div className={styles.difference}>Difference: ${Number(item.reconciliation_difference).toFixed(2)}</div>}</div>
                  <strong>${Number(item.balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>
                  <span className={styles.source}>{item.source}</span>
                  <div className={styles.rowActions}><button className={styles.secondary} type="button" onClick={() => edit(item)}>Edit</button><button className={styles.danger} type="button" onClick={() => remove(item)}>Delete</button></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
